import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import {
  BasePaginationRequest,
  EnvironmentVariables,
} from "@/modules/common/types";
import { AxiosResponse } from "axios";
import {
  EditPropertyPriceDTO,
  AddTrackDTO,
  AddTrackWithCustomerDTO,
  EditFinishingInfoDTO,
  EditRentalDetailsDTO,
  EditPropertyDTO,
  AddSubPropertyDTO,
  AddTrackToPropertyDTO,
  AddListingDTO,
  EditListingDTO,
  ActiveListingDto,
  AddHospitalityListingDTO,
  EditHospitalityListingDTO,
  TrackCommentSource,
} from "../dtos";
import {
  NextActionDto,
  Property,
  Track,
  TrackDocument,
  TrackDocumentType,
  TrackResponse,
  PropertyRental,
  TrackSearchDTO,
  PropertyPrice,
  PropertyFinishing,
  SubProperty,
  Listing,
  InterestJourneyResponse,
  QueryInterestsSearchFilters,
  ScopeOfFinishing,
  RoomTypeDto,
  HospitalityListing,
} from "../types";
import { ActivityType } from "@/modules/lead-page/dtos/index-v2";
import { TrackCommentUpdates, TrackUpdateDto } from "../dtos/track-activity";
import { Contract } from "../dtos/tenants";

export const getTrackByUId = async ({
  uid,
}: {
  uid?: string;
}): Promise<AxiosResponse<Track> | null> => {
  if (!uid) {
    return null;
  }
  const apiUrl = brandUrl<Track>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${uid}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getMentionList = (trackUId: string | undefined) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/mentionable-users`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getTracks = async ({
  filters,
  pagination,
}: {
  filters: TrackSearchDTO;
  pagination: BasePaginationRequest;
}): Promise<TrackResponse> => {
  const apiUrl = brandUrl<Promise<TrackResponse>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks`,
  );

  const params: any = {
    ...filters,
    page: pagination.page,
    pageSize: pagination.pageSize,
    orderBy: pagination.orderBy,
  };

  // Only include orderDirection if provided
  if (pagination.orderDirection) {
    params.orderDirection = pagination.orderDirection;
  }

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const exportTracks = async ({
  filters,
}: {
  filters: TrackSearchDTO;
}) => {
  const apiUrl = brandUrl<{
    url: string;
  }>(`${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-exports`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { ...filters },
    },
  });
};

export const exportSpecificTracks = async ({
  trackUIds,
}: {
  trackUIds: string[];
}) => {
  const apiUrl = brandUrl<{
    url: string;
  }>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-exports/export-by-ids`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { uids: trackUIds },
    },
  });
};

export const addTrack = async (body: AddTrackDTO) => {
  const apiUrl = brandUrl<AxiosResponse<Track>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  }) as Promise<AxiosResponse<Track>>;
};

export const addTrackDetailed = async (body: FormData) => {
  const apiUrl = brandUrl<AxiosResponse<Track>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/detailed`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      formData: true,
      data: body,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  }) as Promise<AxiosResponse<Track>>;
};

export const addTrackWithCustomer = async (body: AddTrackWithCustomerDTO) => {
  const apiUrl = brandUrl<Track>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/with-customer`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  }) as Promise<Track>;
};

export const getTrackNextActions = async (trackId: string) => {
  const apiUrl = brandUrl<NextActionDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackId}/next-actions`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: NextActionDto[] }>;
};

export const markNextActionAsCompleted = async (nextActionId: number) => {
  const apiUrl = brandUrl<void>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/next-actions/${nextActionId}/mark-as-completed`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
    },
  }) as Promise<void>;
};

export const getTrackDocuments = async (trackUId: string) => {
  const apiUrl = brandUrl<TrackDocument[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/track-documents`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: TrackDocument[] }>;
};

export const uploadDocument = async (trackUId: string, formData: FormData) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/track-documents`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      formData: true,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  });
};

export const presignDocument = async (documentUId: string) => {
  const apiUrl = brandUrl<{ data: { url: string } }>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-documents/${documentUId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: { url: string } }>;
};

export const deleteDocument = async (documentUId: string): Promise<any> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-documents/${documentUId}/delete`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  });
};

export const markDocumentAsChosenByClient = async (
  documentUId: string,
  chosenByClient: boolean,
): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-documents/${documentUId}/mark-as-chosen-by-client`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: { chosenByClient },
    },
  });
};

export const downloadDocumentCategory = async ({
  trackUId,
  documentType,
}: {
  trackUId: string;
  documentType: TrackDocumentType;
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/download-document-category`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      responseType: "blob",
      params: {
        documentType,
      },
    },
  }) as Promise<any>;
};

export const editProperty = async (uid: string, body: EditPropertyDTO) => {
  const apiUrl = brandUrl<Property>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  }) as Promise<Property>;
};

export const editPropertyPrice = async (
  uid: string,
  body: EditPropertyPriceDTO,
) => {
  const apiUrl = brandUrl<PropertyPrice>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}/price`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  }) as Promise<PropertyPrice>;
};

export const editFinishingInfo = async (
  uid: string,
  body: EditFinishingInfoDTO,
) => {
  const apiUrl = brandUrl<PropertyFinishing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}/finishing`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  }) as Promise<PropertyFinishing>;
};

export const editRentalDetails = async (
  uid: string,
  body: EditRentalDetailsDTO,
) => {
  const apiUrl = brandUrl<PropertyRental>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}/rental`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  }) as Promise<PropertyRental>;
};

export const getTrackActivityTimeline = async (params: {
  trackUid: string;
  limit: number;
  filter?: ActivityType[];
  cursorDate?: string;
  commentSource?: TrackCommentSource[];
}): Promise<AxiosResponse<TrackUpdateDto>> => {
  const apiUrl = brandUrl<TrackUpdateDto>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/track-updates`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const deleteTrackComment = async (
  trackUId: string,
  commentId: string,
): Promise<any> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/comments/${commentId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const addTrackComment = async ({
  trackUId,
  data,
}: {
  trackUId: string;
  data: {
    comment: string;
    notifySalesAgent?: boolean;
    mentions?: string[];
    source?: TrackCommentSource;
  };
}): Promise<AxiosResponse<TrackCommentUpdates>> => {
  const apiUrl = brandUrl<TrackCommentUpdates>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/comments`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const getSubproperties = async (uid?: string) => {
  if (!uid) {
    return null;
  }
  const apiUrl = brandUrl<AxiosResponse<SubProperty[]>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}/get-sub-properties`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<AxiosResponse<SubProperty[]>>;
};

export const addSubproperty = async (uid: string, body: AddSubPropertyDTO) => {
  const apiUrl = brandUrl<AxiosResponse<Property>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}/create-sub-property`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  }) as Promise<AxiosResponse<Property>>;
};

export const getLinkedTracks = async (uid?: string) => {
  if (!uid) {
    return null;
  }
  const apiUrl = brandUrl<AxiosResponse<Track[]>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${uid}/linked-tracks`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<AxiosResponse<Track[]>>;
};

export const addTrackToProperty = async (
  uid: string,
  payload: AddTrackToPropertyDTO,
) => {
  const apiUrl = brandUrl<AxiosResponse<Track>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/${uid}/create-new-track`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: payload,
    },
  }) as Promise<AxiosResponse<Track>>;
};

export const addListing = async (body: AddListingDTO) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const editListing = async (uid: string, body: EditListingDTO) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings/${uid}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const getActiveListing = async (params: ActiveListingDto) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings/active`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const publishListing = async (uid: string) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings/${uid}/publish`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const markListingAsSold = async (uid: string) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings/${uid}/sold`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const markListingAsAvailable = async (uid: string) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings/${uid}/available`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const unpublishListing = async (uid: string) => {
  const apiUrl = brandUrl<Listing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/listings/${uid}/unpublish`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  }) as Promise<AxiosResponse<Listing>>;
};

export const queryInterestsPerProperty = async ({
  propertyUids,
  filters,
  pagination,
}: {
  propertyUids?: string[];
  filters: QueryInterestsSearchFilters;
  pagination: BasePaginationRequest;
}) => {
  const apiUrl = brandUrl<Promise<AxiosResponse<InterestJourneyResponse>>>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/properties/interests`,
  );

  return fetchFromBrandedUrl<Promise<AxiosResponse<InterestJourneyResponse>>>({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        propertyUids,
        ...filters,
        ...pagination,
      },
    },
  });
};
export const assignTrackToUser = async ({
  trackUId,
  userUId,
}: {
  trackUId: string;
  userUId: string;
}): Promise<AxiosResponse<Track>> => {
  const apiUrl = brandUrl<Track>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${trackUId}/assign-user`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: { userUId },
    },
  });
};

export const assignTracksToUser = async ({
  uids,
  userUId,
}: {
  uids: string[];
  userUId: string;
}): Promise<AxiosResponse<Track>> => {
  const apiUrl = brandUrl<Track>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/bulk/assign-user`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: { userUId, uids },
    },
  });
};

export const editScopeOfFinishing = async (
  uid: string,
  data: { scopeOfFinishing: ScopeOfFinishing },
) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${uid}/scope-of-finishing`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const archiveTrack = async (
  uid: string,
): Promise<AxiosResponse<Track>> => {
  const apiUrl = brandUrl<Track>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${uid}/archive`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  });
};

export const unarchiveTrack = async (
  uid: string,
): Promise<AxiosResponse<Track>> => {
  const apiUrl = brandUrl<Track>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/tracks/${uid}/unarchive`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
    },
  });
};

export const getContractByPropertyUid = async (
  uid: string,
): Promise<AxiosResponse<Contract>> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.CONTRACTS_API_URL}/contracts/property/${uid}?contractType=owner`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<AxiosResponse<Contract>>;
};

export const getRoomTypes = async (): Promise<AxiosResponse<RoomTypeDto[]>> => {
  const apiUrl = brandUrl<RoomTypeDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/room-types`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<AxiosResponse<RoomTypeDto[]>>;
};

export const getLatestHospitalityListing = async (
  propertyUid: string,
): Promise<AxiosResponse<HospitalityListing>> => {
  const apiUrl = brandUrl<HospitalityListing>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/hospitality-listings/latest`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { propertyUid },
    },
  }) as Promise<AxiosResponse<HospitalityListing>>;
};

export const addHospitalityListing = async (
  body: AddHospitalityListingDTO,
): Promise<AxiosResponse> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/hospitality-listings`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: body,
    },
  });
};

export const editHospitalityListing = async (
  uid: string,
  body: EditHospitalityListingDTO,
): Promise<AxiosResponse> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/hospitality-listings/${uid}`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  });
};
